import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { superAdminApi } from "@/api/superAdmin";
import { pharmacyApi } from "@/api/pharmacy";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";

type VendorManualInvoiceRow = {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  invoiceDate?: string;
};

type VendorRow = {
  id: string;
  name: string;
  phone: string;
  createdAt?: string;
  gstNo?: string | null;
  email?: string | null;
  address?: string | null;
  ledger?: {
    totalInvoices: number;
    totalPayments: number;
    balance: number;
  };
};

const VENDOR_INVOICE_EDITOR_ROLES = new Set(["PHARMACIST", "HOSPITAL_ADMIN", "SUPER_ADMIN"]);

const emptyForm = { name: "", phone: "", gstNo: "", email: "", address: "" };

type PharmacyVendorsPageProps = {
  basePath: string;
  adminMode?: boolean;
};

export default function PharmacyVendorsPage({
  basePath,
  adminMode = false,
}: PharmacyVendorsPageProps) {
  const qc = useQueryClient();
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const showHospitalFilter = adminMode && (isSuperAdmin || isChainAdmin);
  const chainHospitals = user?.hospitals ?? [];

  const [selectedHospitalId, setSelectedHospitalId] = useState(
    activeHospitalId || user?.hospitalId || chainHospitals[0]?.id || ""
  );
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<VendorRow | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [invoiceEditVendor, setInvoiceEditVendor] = useState<VendorRow | null>(null);
  const [invoiceAmounts, setInvoiceAmounts] = useState<Record<string, string>>({});

  const canEditVendorInvoices = VENDOR_INVOICE_EDITOR_ROLES.has(user?.role ?? "");
  const canResetVendorBalances =
    user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN";

  const { data: superHospitals = [] } = useQuery({
    queryKey: ["super-admin-hospitals"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: showHospitalFilter && isSuperAdmin,
  });

  const hospitalOptions = useMemo(() => {
    if (isSuperAdmin) return superHospitals as Array<{ id: string; name: string }>;
    return chainHospitals;
  }, [isSuperAdmin, superHospitals, chainHospitals]);

  useEffect(() => {
    if (!showHospitalFilter || selectedHospitalId) return;
    const fallback = activeHospitalId || chainHospitals[0]?.id || hospitalOptions[0]?.id || "";
    if (fallback) setSelectedHospitalId(fallback);
  }, [showHospitalFilter, selectedHospitalId, activeHospitalId, chainHospitals, hospitalOptions]);

  const effectiveHospitalId = showHospitalFilter
    ? selectedHospitalId
    : user?.hospitalId || activeHospitalId || "";
  const hospitalParams = effectiveHospitalId ? { hospitalId: effectiveHospitalId } : undefined;

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["pharmacy-vendors", effectiveHospitalId || "self"],
    queryFn: async () => {
      const res = await pharmacyApi.listVendors(hospitalParams);
      return (res.data as any)?.data ?? [];
    },
    enabled: !!effectiveHospitalId,
  });

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const rows = (vendors as VendorRow[]).filter(
      (v) =>
        !qq ||
        v.name?.toLowerCase().includes(qq) ||
        v.phone?.toLowerCase().includes(qq) ||
        (v.gstNo ?? "").toLowerCase().includes(qq)
    );
    return [...rows].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", "en", { sensitivity: "base" })
    );
  }, [vendors, q]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["pharmacy-vendors"] });
  };

  const { data: invoiceEditRows = [], isLoading: invoiceEditLoading } = useQuery({
    queryKey: ["vendor-invoices-edit", invoiceEditVendor?.id, effectiveHospitalId],
    queryFn: async () => {
      const res = await pharmacyApi.getVendorInvoices(invoiceEditVendor!.id, hospitalParams);
      return ((res.data as any)?.data ?? []) as VendorManualInvoiceRow[];
    },
    enabled: !!invoiceEditVendor?.id && !!effectiveHospitalId,
  });

  useEffect(() => {
    if (!invoiceEditVendor || invoiceEditRows.length === 0) return;
    const map: Record<string, string> = {};
    for (const row of invoiceEditRows) {
      map[row.id] = String(row.totalAmount);
    }
    setInvoiceAmounts(map);
  }, [invoiceEditVendor?.id, invoiceEditRows]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        gstNo: form.gstNo.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim() || undefined,
      };
      if (!payload.name) throw new Error("Vendor name is required");
      if (!/^\d{10}$/.test(payload.phone)) throw new Error("Vendor phone must be 10 digits");
      if (editingVendor) {
        return pharmacyApi.updateVendor(editingVendor.id, payload, hospitalParams);
      }
      return pharmacyApi.createVendor(payload, hospitalParams);
    },
    onSuccess: async () => {
      toast.success(editingVendor ? "Vendor updated" : "Vendor added");
      setOpen(false);
      setEditingVendor(null);
      setForm(emptyForm);
      await invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to save vendor"),
  });

  const saveInvoiceAmountsMutation = useMutation({
    mutationFn: async () => {
      if (!invoiceEditVendor) throw new Error("No vendor selected");
      const rows = invoiceEditRows;
      const updates: Array<{ id: string; totalAmount: number }> = [];
      for (const row of rows) {
        const raw = invoiceAmounts[row.id] ?? String(row.totalAmount);
        const amt = parseFloat(raw);
        if (!amt || amt <= 0 || isNaN(amt)) {
          throw new Error(`Invalid amount for invoice ${row.invoiceNumber}`);
        }
        if (Math.abs(amt - Number(row.totalAmount)) > 0.001) {
          updates.push({ id: row.id, totalAmount: amt });
        }
      }
      if (updates.length === 0) throw new Error("No changes to save");
      await Promise.all(
        updates.map((u) =>
          pharmacyApi.updateVendorInvoice(invoiceEditVendor.id, u.id, { totalAmount: u.totalAmount }, hospitalParams)
        )
      );
    },
    onSuccess: async () => {
      toast.success("Invoice amount(s) updated");
      setInvoiceEditVendor(null);
      setInvoiceAmounts({});
      await invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to update invoice"),
  });

  const deleteMutation = useMutation({
    mutationFn: (vendorId: string) => pharmacyApi.deleteVendor(vendorId, hospitalParams),
    onSuccess: async () => {
      toast.success("Vendor deleted");
      await invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to delete vendor"),
    onSettled: () => setDeletingId(null),
  });

  const openAdd = () => {
    setEditingVendor(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (vendor: VendorRow) => {
    setEditingVendor(vendor);
    setForm({
      name: vendor.name,
      phone: vendor.phone,
      gstNo: vendor.gstNo ?? "",
      email: vendor.email ?? "",
      address: vendor.address ?? "",
    });
    setOpen(true);
  };

  const openInvoiceAmountEdit = (vendor: VendorRow) => {
    if ((vendor.ledger?.totalInvoices ?? 0) <= 0 && (vendor.ledger?.totalPayments ?? 0) <= 0) {
      toast.message("No manual invoices on this vendor yet");
      return;
    }
    setInvoiceAmounts({});
    setInvoiceEditVendor(vendor);
  };

  const resetBalancesMutation = useMutation({
    mutationFn: async () => {
      const res = await pharmacyApi.resetVendorOutstandingBalances(
        { confirm: "RESET_OUTSTANDING" },
        hospitalParams
      );
      return (res.data as any)?.data as { vendorsSettled: number };
    },
    onSuccess: async (data) => {
      toast.success(
        data?.vendorsSettled
          ? `Recorded settlement payments for ${data.vendorsSettled} vendor(s). Outstanding is now ₹0.`
          : "No outstanding balances to reset."
      );
      await invalidate();
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to reset outstanding balances"
      ),
  });

  const handleResetAllBalances = () => {
    const ok = window.confirm(
      "Record settlement payments for every vendor with outstanding balance at this hospital?\n\n" +
        "This sets balance to ₹0 (invoiced totals stay the same). Use after correcting invoice amounts. " +
        "New invoices and payments will update the ledger normally going forward."
    );
    if (!ok) return;
    resetBalancesMutation.mutate();
  };

  const handleDelete = (vendor: VendorRow) => {
    const ok = window.confirm(
      `Delete vendor "${vendor.name}"?\n\nOnly vendors with no invoices, payments, or stock-in history can be deleted.`
    );
    if (!ok) return;
    setDeletingId(vendor.id);
    deleteMutation.mutate(vendor.id);
  };

  const selectedHospitalName =
    hospitalOptions.find((h) => h.id === effectiveHospitalId)?.name ?? "Selected hospital";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Pharmacy vendors</CardTitle>
            <CardDescription className="mt-1">
              Purchase vendor master and outstanding ledger (invoices − payments). Open a vendor to
              record payments, upload invoices, and reconcile stock-in.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {canResetVendorBalances && effectiveHospitalId ? (
              <Button
                type="button"
                variant="outline"
                disabled={resetBalancesMutation.isPending}
                onClick={handleResetAllBalances}
              >
                {resetBalancesMutation.isPending ? "Resetting…" : "Reset all outstanding to ₹0"}
              </Button>
            ) : null}
            <Button onClick={openAdd} disabled={!effectiveHospitalId}>
              <Plus className="h-4 w-4 mr-2" />
              Add vendor
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            {showHospitalFilter ? (
              <div className="space-y-1 min-w-[240px]">
                <Label className="text-xs text-muted-foreground">Hospital unit</Label>
                <Select value={selectedHospitalId} onValueChange={setSelectedHospitalId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hospital" />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitalOptions.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : adminMode ? (
              <div className="text-sm text-muted-foreground">
                Hospital: <span className="font-medium text-foreground">{selectedHospitalName}</span>
              </div>
            ) : null}

            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name / phone / GST"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2">Vendor</th>
                  <th className="text-left p-2">Onboarded</th>
                  <th className="text-left p-2">Phone</th>
                  <th className="text-left p-2">GST No</th>
                  <th className="text-right p-2">Invoiced</th>
                  <th className="text-right p-2">Paid</th>
                  <th className="text-right p-2">Balance</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!effectiveHospitalId ? (
                  <tr>
                    <td colSpan={8} className="p-3 text-muted-foreground">
                      Select a hospital to view vendors.
                    </td>
                  </tr>
                ) : isLoading ? (
                  <tr>
                    <td colSpan={8} className="p-3 text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-3 text-muted-foreground">
                      No vendors found
                    </td>
                  </tr>
                ) : (
                  filtered.map((v) => (
                    <tr key={v.id} className="border-t">
                      <td className="p-2 font-medium">{v.name}</td>
                      <td className="p-2 text-muted-foreground whitespace-nowrap">
                        {v.createdAt ? formatDate(v.createdAt) : "—"}
                      </td>
                      <td className="p-2">{v.phone}</td>
                      <td className="p-2">{v.gstNo || "—"}</td>
                      <td className="p-2 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          <span>₹{Number(v.ledger?.totalInvoices ?? 0).toFixed(2)}</span>
                          {canEditVendorInvoices && (v.ledger?.totalInvoices ?? 0) > 0 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 shrink-0"
                              title="Edit manual invoice totals (updates outstanding balance)"
                              onClick={() => openInvoiceAmountEdit(v)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        ₹{Number(v.ledger?.totalPayments ?? 0).toFixed(2)}
                      </td>
                      <td
                        className={`p-2 text-right font-medium ${
                          Number(v.ledger?.balance ?? 0) > 0 ? "text-amber-700" : ""
                        }`}
                      >
                        ₹{Number(v.ledger?.balance ?? 0).toFixed(2)}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={
                              adminMode
                                ? `${basePath}/pharmacy-vendors/${v.id}${
                                    effectiveHospitalId ? `?hospitalId=${effectiveHospitalId}` : ""
                                  }`
                                : `${basePath}/vendors/${v.id}`
                            }
                            className="text-blue-600 hover:underline"
                          >
                            Ledger
                          </Link>
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(v)}>
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            disabled={deletingId === v.id}
                            onClick={() => handleDelete(v)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!invoiceEditVendor}
        onOpenChange={(next) => {
          if (!next) {
            setInvoiceEditVendor(null);
            setInvoiceAmounts({});
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit invoiced amount</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {invoiceEditVendor?.name} — change manual invoice totals at any time. Outstanding =
            total invoiced − total paid (updates after save).
          </p>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {invoiceEditLoading ? (
              <p className="text-sm text-muted-foreground">Loading invoices…</p>
            ) : invoiceEditRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No manual invoices for this vendor.</p>
            ) : (
              invoiceEditRows.map((inv) => (
              <div key={inv.id} className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Invoice {inv.invoiceNumber}
                  {inv.invoiceDate ? ` · ${String(inv.invoiceDate).slice(0, 10)}` : ""}
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={invoiceAmounts[inv.id] ?? String(inv.totalAmount)}
                  onChange={(e) =>
                    setInvoiceAmounts((m) => ({ ...m, [inv.id]: e.target.value }))
                  }
                />
              </div>
            ))
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setInvoiceEditVendor(null);
                setInvoiceAmounts({});
              }}
              disabled={saveInvoiceAmountsMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveInvoiceAmountsMutation.mutate()}
              disabled={saveInvoiceAmountsMutation.isPending}
            >
              {saveInvoiceAmountsMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setEditingVendor(null);
            setForm(emptyForm);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVendor ? "Edit vendor" : "Add vendor"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1 md:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Mobile (10 digits) *</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>GST No</Label>
              <Input value={form.gstNo} onChange={(e) => setForm((f) => ({ ...f, gstNo: e.target.value }))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Address</Label>
              <Textarea rows={3} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : editingVendor ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
